import {
  ProjectDto,
  ProjectsDto,
  CreateProjectDto,
  PositionDto,
  AddPositionDto,
  EmployeeDto,
  AddEmployeeDto,
  AddEpicDto,
  EpicDto,
  AddEpicGroupDto,
  EpicGroupDto,
  AddColumnDto,
  ColumnDto,
  AddTableDto,
  TableDto,
  AddSheetDto,
  SheetDto,
  ReportDto,
  MetricDto,
  GetMetricsDto,
  SummaryMetricsDto,
  GetSummaryMetricsDto,
} from '@/core/http.projects';
import { api } from '../api';
import { projectsHttp } from '@/core/http.projects';
import { rtkq, RtkqRequest } from '../rtkq';

export type GetProjectsResponse = ProjectsDto;
export type GetProjectResponse = ProjectDto;
export type AddProjectResponse = ProjectDto;
export type CreateProjectArgs = CreateProjectDto;
export type UpdateProjectsArgs = { value: CreateProjectDto } & { id: string };
export type DeleteProjectsArgs = { id: string };
export type GetProjectArgs = { id: string };
export type AddPositionResponse = PositionDto;
export type AddPositionArgs = { positionData: AddPositionDto } & { projectId: string };
export type UpdatePositionArgs = { positionData: AddPositionDto } & { id: string };
export type DeletePositionArgs = { id: string };
export type AddEmployeeResponse = EmployeeDto;
export type AddEmployeeArgs = { employeeData: AddEmployeeDto } & { projectId: string };
export type UpdateEmployeeArgs = { employeeData: AddEmployeeDto } & { id: string };
export type DeleteEmployeeArgs = { id: string };
export type AddEpicArgs = { epicData: AddEpicDto } & { projectId: string };
export type AddEpicResponse = EpicDto;
export type UpdateEpicArgs = { epicData: AddEpicDto } & { id: string };
export type DeleteEpicArgs = { id: string };
export type AddEpicGroupArgs = { epicGroupData: AddEpicGroupDto } & { projectId: string };
export type AddEpicGroupResponse = EpicGroupDto;
export type UpdateEpicGroupArgs = { epicGroupData: AddEpicGroupDto } & { id: string };
export type DeleteEpicGroupArgs = { id: string };
export type AddColumnArgs = { columnData: AddColumnDto } & { projectId: string };
export type AddColumnResponse = ColumnDto;
export type UpdateColumnArgs = { columnData: AddColumnDto } & { id: string };
export type DeleteColumnArgs = { id: string };
export type AddTableArgs = { tableData: AddTableDto } & { projectId: string };
export type AddTableResponse = TableDto;
export type UpdateTableArgs = { tableData: AddTableDto } & { id: string };
export type DeleteTableArgs = { id: string };
export type AddSheetArgs = { sheetData: AddSheetDto } & { projectId: string };
export type AddSheetResponse = SheetDto;
export type UpdateSheetArgs = { sheetData: AddSheetDto } & { id: string };
export type DeleteSheetArgs = { id: string };
export type AddReportArgs = { reportData: FormData } & { projectId: string; reportName: string };
export type AddReportResponse = ReportDto;
export type DeleteReportArgs = { id: string };
export type FinishConstructorArgs = { id: string };
export type GetMetricsResponse = MetricDto[];
export type GetMetricsArgs = GetMetricsDto;
export type GetSummaryMetricsResponse = SummaryMetricsDto;
export type GetSummaryMetricsArgs = GetSummaryMetricsDto;

export const projectsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProjects: build.query<GetProjectsResponse, RtkqRequest<void>>({
      queryFn: async (args) => {
        return rtkq(args).exec(() => projectsHttp.getProjects());
      },
      providesTags: ['projects'],
    }),
    getProject: build.query<GetProjectResponse, RtkqRequest<GetProjectArgs>>({
      queryFn: async (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.getProject(id));
      },
      providesTags: ['projects'],
    }),
    addProject: build.mutation<AddProjectResponse, RtkqRequest<CreateProjectDto>>({
      queryFn: (args) => {
        return rtkq(args).exec(() => projectsHttp.addProject(args));
      },
      invalidatesTags: ['projects'],
    }),
    updateProject: build.mutation<void, RtkqRequest<UpdateProjectsArgs>>({
      queryFn: (args) => {
        const { id, value } = args;
        return rtkq(args).exec(() => projectsHttp.updateProject(value, id));
      },
      invalidatesTags: ['projects'],
    }),
    deleteProject: build.mutation<void, RtkqRequest<DeleteProjectsArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deleteProject(id));
      },
      invalidatesTags: ['projects'],
    }),
    addPosition: build.mutation<AddPositionResponse, RtkqRequest<AddPositionArgs>>({
      queryFn: (args) => {
        const { positionData, projectId } = args;
        return rtkq(args).exec(() => projectsHttp.addPosition(positionData, projectId));
      },
      invalidatesTags: ['projects'],
    }),
    updatePosition: build.mutation<void, RtkqRequest<UpdatePositionArgs>>({
      queryFn: (args) => {
        const { positionData, id } = args;
        return rtkq(args).exec(() => projectsHttp.updatePosition(positionData, id));
      },
      invalidatesTags: ['projects'],
    }),
    deletePosition: build.mutation<void, RtkqRequest<DeletePositionArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deletePosition(id));
      },
      invalidatesTags: ['projects'],
    }),
    addEmployee: build.mutation<AddEmployeeResponse, RtkqRequest<AddEmployeeArgs>>({
      queryFn: (args) => {
        const { employeeData, projectId } = args;
        return rtkq(args).exec(() => projectsHttp.addEmployee(employeeData, projectId));
      },
      invalidatesTags: ['projects'],
    }),
    updateEmployee: build.mutation<void, RtkqRequest<UpdateEmployeeArgs>>({
      queryFn: (args) => {
        const { employeeData, id } = args;
        return rtkq(args).exec(() => projectsHttp.updateEmployee(employeeData, id));
      },
      invalidatesTags: ['projects'],
    }),
    deleteEmployee: build.mutation<void, RtkqRequest<DeleteEmployeeArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deleteEmployee(id));
      },
      invalidatesTags: ['projects'],
    }),
    addEpic: build.mutation<AddEpicResponse, RtkqRequest<AddEpicArgs>>({
      queryFn: (args) => {
        const { epicData, projectId } = args;
        return rtkq(args).exec(() => projectsHttp.addEpic(epicData, projectId));
      },
      invalidatesTags: ['projects'],
    }),
    updateEpic: build.mutation<void, RtkqRequest<UpdateEpicArgs>>({
      queryFn: (args) => {
        const { epicData, id } = args;
        return rtkq(args).exec(() => projectsHttp.updateEpic(epicData, id));
      },
      invalidatesTags: ['projects'],
    }),
    deleteEpic: build.mutation<void, RtkqRequest<DeleteEpicArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deleteEpic(id));
      },
      invalidatesTags: ['projects'],
    }),
    addEpicGroup: build.mutation<AddEpicGroupResponse, RtkqRequest<AddEpicGroupArgs>>({
      queryFn: (args) => {
        const { epicGroupData, projectId } = args;
        return rtkq(args).exec(() => projectsHttp.addEpicGroup(epicGroupData, projectId));
      },
      invalidatesTags: ['projects'],
    }),
    updateEpicGroup: build.mutation<void, RtkqRequest<UpdateEpicGroupArgs>>({
      queryFn: (args) => {
        const { epicGroupData, id } = args;
        return rtkq(args).exec(() => projectsHttp.updateEpicGroup(epicGroupData, id));
      },
      invalidatesTags: ['projects'],
    }),
    deleteEpicGroup: build.mutation<void, RtkqRequest<DeleteEpicGroupArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deleteEpicGroup(id));
      },
      invalidatesTags: ['projects'],
    }),
    addColumn: build.mutation<AddColumnResponse, RtkqRequest<AddColumnArgs>>({
      queryFn: (args) => {
        const { columnData, projectId } = args;
        return rtkq(args).exec(() => projectsHttp.addColumn(columnData, projectId));
      },
      invalidatesTags: ['projects'],
    }),
    updateColumn: build.mutation<void, RtkqRequest<UpdateColumnArgs>>({
      queryFn: (args) => {
        const { columnData, id } = args;
        return rtkq(args).exec(() => projectsHttp.updateColumn(columnData, id));
      },
      invalidatesTags: ['projects'],
    }),
    deleteColumn: build.mutation<void, RtkqRequest<DeleteColumnArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deleteColumn(id));
      },
      invalidatesTags: ['projects'],
    }),
    addTable: build.mutation<AddTableResponse, RtkqRequest<AddTableArgs>>({
      queryFn: (args) => {
        const { tableData, projectId } = args;
        return rtkq(args).exec(() => projectsHttp.addTable(tableData, projectId));
      },
      invalidatesTags: ['projects'],
    }),
    updateTable: build.mutation<void, RtkqRequest<UpdateTableArgs>>({
      queryFn: (args) => {
        const { tableData, id } = args;
        return rtkq(args).exec(() => projectsHttp.updateTable(tableData, id));
      },
      invalidatesTags: ['projects'],
    }),
    deleteTable: build.mutation<void, RtkqRequest<DeleteTableArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deleteTable(id));
      },
      invalidatesTags: ['projects'],
    }),
    addSheet: build.mutation<AddSheetResponse, RtkqRequest<AddSheetArgs>>({
      queryFn: (args) => {
        const { sheetData, projectId } = args;
        return rtkq(args).exec(() => projectsHttp.addSheet(sheetData, projectId));
      },
      invalidatesTags: ['projects'],
    }),
    updateSheet: build.mutation<void, RtkqRequest<UpdateSheetArgs>>({
      queryFn: (args) => {
        const { sheetData, id } = args;
        return rtkq(args).exec(() => projectsHttp.updateSheet(sheetData, id));
      },
      invalidatesTags: ['projects'],
    }),
    deleteSheet: build.mutation<void, RtkqRequest<DeleteSheetArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deleteSheet(id));
      },
      invalidatesTags: ['projects'],
    }),
    addReport: build.mutation<AddReportResponse, RtkqRequest<AddReportArgs>>({
      queryFn: (args) => {
        const { reportData, projectId, reportName } = args;
        return rtkq(args).execWithoutNotify(() => projectsHttp.addReport(reportData, projectId, reportName));
      },
      invalidatesTags: ['projects'],
    }),
    deleteReport: build.mutation<void, RtkqRequest<DeleteReportArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.deleteReport(id));
      },
      invalidatesTags: ['projects'],
    }),
    finishConstructor: build.mutation<void, RtkqRequest<FinishConstructorArgs>>({
      queryFn: (args) => {
        const { id } = args;
        return rtkq(args).exec(() => projectsHttp.finishConstructor(id));
      },
      invalidatesTags: ['projects'],
    }),
    getMetrics: build.query<GetMetricsResponse, RtkqRequest<GetMetricsArgs>>({
      queryFn: async (args) => {
        const { id, startDate, endDate, type } = args;
        return rtkq(args).exec(() => projectsHttp.getMetrics({ id, startDate, endDate, type }));
      },
      providesTags: ['projects'],
    }),
    getSummaryMetrics: build.query<GetSummaryMetricsResponse, RtkqRequest<GetSummaryMetricsArgs>>({
      queryFn: async (args) => {
        const { id, startDate, endDate } = args;
        return rtkq(args).exec(() => projectsHttp.getSummaryMetrics({ id, startDate, endDate }));
      },
      providesTags: ['projects'],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useAddProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectMutation,
  useAddPositionMutation,
  useUpdatePositionMutation,
  useDeletePositionMutation,
  useAddEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useAddEpicMutation,
  useUpdateEpicMutation,
  useDeleteEpicMutation,
  useAddEpicGroupMutation,
  useUpdateEpicGroupMutation,
  useDeleteEpicGroupMutation,
  useAddColumnMutation,
  useUpdateColumnMutation,
  useDeleteColumnMutation,
  useAddTableMutation,
  useUpdateTableMutation,
  useDeleteTableMutation,
  useAddSheetMutation,
  useUpdateSheetMutation,
  useDeleteSheetMutation,
  useAddReportMutation,
  useDeleteReportMutation,
  useFinishConstructorMutation,
  useGetMetricsQuery,
  useGetSummaryMetricsQuery,
} = projectsApi;
